import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Megaphone, Users, Mail, MousePointerClick, UserMinus, RefreshCw, Globe, CalendarDays, BarChart3, LayoutDashboard } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { CampaignBuilder, MarketingCalendar, CampaignReports, StatusBadge } from '../components/MarketingCampaigns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pct = (v) => (v == null ? 'N/A' : `${(v * 100).toFixed(1)}%`);

const Stat = ({ icon: Icon, label, value, tone = 'text-primary' }) => (
  <Card><CardContent className="p-4 flex items-center gap-3">
    <div className={`h-10 w-10 rounded-xl bg-muted flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
    <div><p className="text-xs text-muted-foreground">{label}</p><p className="text-xl font-bold">{value ?? 'N/A'}</p></div>
  </CardContent></Card>
);

export default function MarketingPage() {
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [syncLog, setSyncLog] = useState([]);
  const [listId, setListId] = useState('');
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([axios.get(`${API}/mailchimp/status`), axios.get(`${API}/mailchimp/overview`)]);
      setStatus(s.data); setOverview(o.data);
      if (s.data.connected) {
        const [a, c, m, l] = await Promise.all([
          axios.get(`${API}/mailchimp/audiences`), axios.get(`${API}/mailchimp/campaigns?count=50`),
          axios.get(`${API}/mailchimp/metrics`), axios.get(`${API}/mailchimp/sync-log`),
        ]);
        setAudiences(a.data); setCampaigns(c.data.campaigns || []); setMetrics(m.data); setSyncLog(l.data);
        if (a.data.length) setListId(prev => prev || a.data[0].id);
      }
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Admin access required' : 'Failed to load marketing data');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncContacts = async () => {
    if (!listId) return toast.error('Select an audience');
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/mailchimp/sync-contacts`, { list_id: listId });
      toast.success(`Synced ${r.data.synced} contacts — ${r.data.skipped_unsubscribed} unsubscribed respected, ${r.data.failed} failed`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const chartData = (metrics?.campaigns || []).slice(0, 8).reverse().map(r => ({
    name: (r.title || '').slice(0, 14) || 'Campaign',
    'Open %': r.opens?.open_rate != null ? +(r.opens.open_rate * 100).toFixed(1) : 0,
    'Click %': r.clicks?.click_rate != null ? +(r.clicks.click_rate * 100).toFixed(1) : 0,
  }));

  const connected = !!status?.connected;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="marketing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone className="h-6 w-6 text-primary" /> Marketing Hub</h1>
          <p className="text-sm text-muted-foreground">Campaigns, audiences and email performance via Mailchimp</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={connected ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'} data-testid="marketing-connection-badge">
            {connected ? `Mailchimp: ${status.account_name || 'connected'}` : 'Mailchimp: not connected'}
          </Badge>
          <Button size="sm" variant="ghost" onClick={load} className="gap-1"><RefreshCw className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {!connected && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-500/5" data-testid="marketing-not-connected">
          <CardContent className="p-4 text-sm">
            <p className="font-semibold text-amber-700">Mailchimp is not connected — no campaign data is shown (nothing is simulated).</p>
            <p className="text-amber-700/80 mt-1">Ask the assistant to "add my Mailchimp key" (Mailchimp → Account → Extras → API keys, format ends in <code>-us21</code>). Local CRM lead stats below remain live.</p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="dashboard">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" className="gap-1.5" data-testid="marketing-tab-dashboard"><LayoutDashboard className="h-4 w-4" /> Dashboard</TabsTrigger>
          <TabsTrigger value="audience" className="gap-1.5" data-testid="marketing-tab-audience"><Users className="h-4 w-4" /> Audience</TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-1.5" data-testid="marketing-tab-campaigns"><Mail className="h-4 w-4" /> Campaigns</TabsTrigger>
          <TabsTrigger value="calendar" className="gap-1.5" data-testid="marketing-tab-calendar"><CalendarDays className="h-4 w-4" /> Calendar</TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5" data-testid="marketing-tab-reports"><BarChart3 className="h-4 w-4" /> Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Mail} label="Emails Sent" value={metrics?.available ? metrics.total_emails_sent : connected ? 0 : 'N/A'} />
            <Stat icon={MousePointerClick} label="Avg Open / Click" value={metrics?.available ? `${pct(metrics.avg_open_rate)} / ${pct(metrics.avg_click_rate)}` : 'N/A'} tone="text-emerald-600" />
            <Stat icon={UserMinus} label="Unsubscribes" value={metrics?.available ? metrics.total_unsubscribes : 'N/A'} tone="text-red-500" />
            <Stat icon={Users} label="Mailchimp Subscribers" value={connected ? (status.total_subscribers ?? 'N/A') : 'N/A'} tone="text-blue-600" />
          </div>
          {chartData.length > 0 && (
            <Card data-testid="marketing-performance-chart">
              <CardHeader className="pb-2"><CardTitle className="text-base">Campaign Performance</CardTitle></CardHeader>
              <CardContent style={{ minHeight: 260 }}>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} unit="%" /><Tooltip />
                    <Bar dataKey="Open %" fill="#eab308" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Click %" fill="#0f172a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Users} label="Eligible CRM Contacts" value={overview?.eligible_contacts} />
            <Stat icon={Globe} label="Leads (last 7 days)" value={overview?.leads_last_7_days} tone="text-emerald-600" />
            <Stat icon={Users} label="Total Leads" value={overview?.total_leads} tone="text-blue-600" />
            <Stat icon={UserMinus} label="Suppressed (opted out)" value={overview?.suppressed_unsubscribed} tone="text-red-500" />
          </div>
          {overview?.leads_by_source && Object.keys(overview.leads_by_source).length > 0 && (
            <Card data-testid="leads-by-source-card">
              <CardHeader className="pb-2"><CardTitle className="text-base">Lead Generation by Source</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {Object.entries(overview.leads_by_source).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
                  <Badge key={s} variant="outline" className="text-sm py-1 px-3 capitalize">{s}: <b className="ml-1">{n}</b></Badge>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="audience" className="space-y-4">
          <Card>
            <CardContent className="p-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1 min-w-[220px]">
                <p className="text-xs text-muted-foreground">Target audience</p>
                <Select value={listId} onValueChange={setListId} disabled={!connected}>
                  <SelectTrigger data-testid="audience-select"><SelectValue placeholder={connected ? 'Select audience' : 'Not connected'} /></SelectTrigger>
                  <SelectContent>{audiences.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.stats?.member_count ?? '?'})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button size="sm" className="gap-1.5" disabled={!connected || syncing} onClick={syncContacts} data-testid="sync-contacts-btn">
                <Users className="h-4 w-4" /> {syncing ? 'Syncing…' : `Sync ${overview?.eligible_contacts ?? ''} CRM Contacts`}
              </Button>
              <p className="text-xs text-muted-foreground w-full">POPIA: unsubscribed and cleaned contacts ({overview?.suppressed_unsubscribed ?? 0}) are never re-added or emailed.</p>
            </CardContent>
          </Card>
          <Card data-testid="audiences-table">
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow><TableHead>Audience</TableHead><TableHead className="text-right">Members</TableHead><TableHead className="text-right">Unsubscribed</TableHead></TableRow></TableHeader>
                <TableBody>
                  {audiences.map(a => (
                    <TableRow key={a.id}><TableCell className="font-medium text-sm">{a.name}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{a.stats?.member_count ?? 'N/A'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{a.stats?.unsubscribe_count ?? 'N/A'}</TableCell></TableRow>
                  ))}
                  {audiences.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-8 text-muted-foreground">{connected ? 'No audiences in your Mailchimp account' : 'Connect Mailchimp to see audiences'}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          {syncLog.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Sync History</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {syncLog.slice(0, 8).map(l => (
                  <p key={l.id} className="text-xs text-muted-foreground">
                    {new Date(l.created_at).toLocaleString('en-ZA')} — {l.synced} synced, {l.skipped_unsubscribed} unsubscribed skipped, {l.failed} failed (by {l.by})
                  </p>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <div className="flex justify-end"><CampaignBuilder audiences={audiences} connected={connected} onDone={load} /></div>
          <Card data-testid="campaigns-table">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Campaign</TableHead><TableHead>Subject</TableHead><TableHead>Audience</TableHead><TableHead>Status</TableHead><TableHead>Send Time</TableHead><TableHead className="text-right">Sent</TableHead></TableRow></TableHeader>
                <TableBody>
                  {campaigns.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="text-sm font-medium">{c.title || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{c.subject || '-'}</TableCell>
                      <TableCell className="text-sm">{c.list_name || '-'}</TableCell>
                      <TableCell><StatusBadge status={c.status} /></TableCell>
                      <TableCell className="text-sm">{c.send_time ? new Date(c.send_time).toLocaleString('en-ZA') : '-'}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{c.emails_sent ?? 0}</TableCell>
                    </TableRow>
                  ))}
                  {campaigns.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">{connected ? 'No campaigns yet — create your first one' : 'Connect Mailchimp to manage campaigns'}</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <MarketingCalendar campaigns={campaigns} />
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <p className="text-xs text-muted-foreground">Metrics come directly from Mailchimp. "N/A" means the metric is unavailable (not zero). Conversion tracking requires e-commerce integration in your Mailchimp account.</p>
          <CampaignReports campaigns={campaigns} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
